import React, { useEffect, useState, useRef } from "react";
import { Formik, Form, useField, ErrorMessage } from "formik";
import * as Yup from "yup";
import { createPost } from "../utils/networkApi";
import Loader from "./Loader";
import Swal from 'sweetalert2';
import Translate from "./Translate";
import { useTranslation } from "react-i18next";

const initialValues = {
  name: "",
  address: "",
  mobile: "",
  subject: "",
  category: "RTI Related",
  email: "",
  pincode: "",
  text: "",
  captcha: "",
  file: null,
};

const AccessibleErrorMessage = ({ name, id }) => {
  return (
    <ErrorMessage name={name}>
      {(msg) => (
        <div
          id={id}
          role="alert"
          aria-live="polite"
          className="text-red-600 text-sm mt-1 flex items-start gap-2"
        >
          <span className="sr-only">Error: </span>
          <span aria-hidden="true" className="text-red-600 font-bold">!</span>
          <span>{msg}</span>
        </div>
      )}
    </ErrorMessage>
  );
};

const AccessibleField = ({ name, id, errorId, describedByIds = [], as, className = "", ...props }) => {
  const [field, meta] = useField(name);
  const hasError = Boolean(meta.touched && meta.error);
  const Component = as || "input";
  const describedBy = [
    ...describedByIds.filter(Boolean),
    ...(hasError ? [errorId] : []),
  ].join(" ") || undefined;

  return (
    <>
      <Component
        {...field}
        {...props}
        id={id}
        name={name}
        aria-invalid={hasError ? "true" : "false"}
        aria-describedby={describedBy}
        className={`${className} ${hasError ? "border-red-500 focus:ring-red-500" : ""}`}
      />
      {hasError ? <AccessibleErrorMessage name={name} id={errorId} /> : null}
    </>
  );
};


export default function GrievanceFormBase() {
  const { t } = useTranslation();

  const validationSchema = Yup.object({
    name: Yup.string()
      .required(t("val-name-required"))
      .min(2, t("val-name-short")),
    address: Yup.string()
      .required(t("val-address-required")),
    mobile: Yup.string()
      .required(t("val-mobile-required"))
      .matches(/^\d{10}$/, t("val-mobile-invalid")),
    subject: Yup.string()
      .required(t("val-subject-required")),
    category: Yup.string()
      .required(t("val-category-required")),
    email: Yup.string()
      .required(t("val-email-required"))
      .email(t("val-email-invalid")),
    pincode: Yup.string()
      .required(t("val-pincode-required"))
      .matches(/^\d{6}$/, t("val-pincode-invalid")),
    text: Yup.string()
      .required(t("val-text-required")),
    captcha: Yup.string()
      .required(t("val-captcha-required")),
  });

  const canvasRef = useRef(null);
  const [captcha, setCaptcha] = useState("");
  const [loading, setLoading] = useState(false);

  const generateCaptchaText = (length = 5) => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
    let text = "";
    for (let i = 0; i < length; i++) {
      text += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return text;
  };

  const generateCaptcha = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const captchaText = generateCaptchaText();
    setCaptcha(captchaText);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#f9fafb";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.font = "20px monospace";
    ctx.fillStyle = "#1f2937";
    ctx.textBaseline = "middle";
    for (let i = 0; i < captchaText.length; i++) {
      const x = 15 + i * 22;
      const y = canvas.height / 2;
      const angle = (Math.random() - 0.5) * 0.4;
      ctx.save();
      ctx.translate(x, y);
      ctx.rotate(angle);
      ctx.fillText(captchaText[i], 0, 0);
      ctx.restore();
    }
    for (let i = 0; i < 4; i++) {
      ctx.strokeStyle = "#9ca3af";
      ctx.beginPath();
      ctx.moveTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.lineTo(Math.random() * canvas.width, Math.random() * canvas.height);
      ctx.stroke();
    }
  };

  useEffect(() => {
    generateCaptcha();
  }, []);

  const handleRefreshCaptcha = () => {
    generateCaptcha();
  };

  const handleSubmit = async (values, { setSubmitting, resetForm, setFieldError }) => {
    try {
      if (values.captcha !== captcha) {
        setFieldError("captcha", t("val-captcha-mismatch"));
        generateCaptcha();
        return false;
      }
      setSubmitting(true);
      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        if (key === "file" && value) {
          formData.append(key, value);
        } else if (key !== "file") {
          formData.append(key, value);
        }
      });
      setLoading(true);
      const response = await createPost('grievance/send', formData);
      setLoading(false);
      if (response.success) {
        resetForm();
        Swal.fire({
          title: t('success-title'),
          text: response.message,
          icon: 'success',
          confirmButtonText: t('ok-button')
        });
        generateCaptcha();
      }
    } catch (error) {
      setLoading(false);
      console.error("Submission error:", error);
      alert(t("failed-submit"));
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full">
      {loading && <Loader />}
      <section aria-labelledby="form-heading" className="w-full max-w-7xl mx-auto px-4 mt-8">
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ setFieldValue }) => (
            <Form className="bg-white rounded-lg shadow-lg border border-primary-100 p-6 md:p-10" noValidate>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <fieldset className="border-0 p-0 m-0 min-w-0 flex flex-col gap-4 w-full">
                  <legend className="sr-only">{t('personal-information')}</legend>
                  <div>
                    <label htmlFor="name" className="block font-semibold text-primary-700 mb-1 text-left">
                      <Translate text={"name"} /> <span className="text-red-600" aria-hidden="true">*</span>
                    </label>
                    <AccessibleField
                      type="text"
                      id="name"
                      name="name"
                      placeholder={t("name-placeholder")}
                      errorId="name-error"
                      aria-required="true"
                      describedByIds={["name-help"]}
                      className="w-full border border-primary-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="mobile" className="block font-semibold text-primary-700 mb-1 text-left">
                      <Translate text={"mobile"} /> <span className="text-red-600" aria-hidden="true">*</span>
                    </label>
                    <AccessibleField
                      type="tel"
                      id="mobile"
                      name="mobile"
                      placeholder={t("mobile-placeholder")}
                      errorId="mobile-error"
                      aria-required="true"
                      describedByIds={["mobile-help"]}
                      inputMode="numeric"
                      maxLength={10}
                      className="w-full border border-primary-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block font-semibold text-primary-700 mb-1 text-left">
                      <Translate text={"email"} /> <span className="text-red-600" aria-hidden="true">*</span>
                    </label>
                    <AccessibleField
                      type="email"
                      id="email"
                      name="email"
                      placeholder={t("email-placeholder")}
                      errorId="email-error"
                      aria-required="true"
                      describedByIds={["email-help"]}
                      inputMode="email"
                      className="w-full border border-primary-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="address" className="block font-semibold text-primary-700 mb-1 text-left">
                      <Translate text={"address"} /> <span className="text-red-600" aria-hidden="true">*</span>
                    </label>
                    <AccessibleField
                      type="text"
                      id="address"
                      name="address"
                      placeholder={t("address-placeholder")}
                      errorId="address-error"
                      aria-required="true"
                      describedByIds={["address-help"]}
                      className="w-full border border-primary-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="pincode" className="block font-semibold text-primary-700 mb-1 text-left">
                      <Translate text={"pincode"} /> <span className="text-red-600" aria-hidden="true">*</span>
                    </label>
                    <AccessibleField
                      type="text"
                      id="pincode"
                      name="pincode"
                      placeholder={t("pincode-placeholder")}
                      errorId="pincode-error"
                      aria-required="true"
                      describedByIds={["pincode-help"]}
                      inputMode="numeric"
                      maxLength={6}
                      className="w-full border border-primary-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                </fieldset>
                <fieldset className="border-0 p-0 m-0 min-w-0 flex flex-col gap-4 w-full">
                  <legend className="sr-only">{t('grievance-details')}</legend>
                  <div>
                    <label htmlFor="category" className="block font-semibold text-primary-700 mb-1 text-left">
                      <Translate text={"select-category"} /> <span className="text-red-600" aria-hidden="true">*</span>
                    </label>
                    <AccessibleField
                      as="select"
                      id="category"
                      name="category"
                      errorId="category-error"
                      aria-required="true"
                      describedByIds={["category-help"]}
                      className="w-full border border-primary-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    >
                      <option value="">{t('select-category-option')}</option>
                      <option value="RTI Related">{t('cat-rti')}</option>
                      <option value="General">{t('cat-general')}</option>
                      <option value="Technical">{t('cat-technical')}</option>
                      <option value="Other">{t('cat-other')}</option>
                    </AccessibleField>
                  </div>
                  <div>
                    <label htmlFor="subject" className="block font-semibold text-primary-700 mb-1 text-left">
                      <Translate text={"grievance-subject"} /> <span className="text-red-600" aria-hidden="true">*</span>
                    </label>
                    <AccessibleField
                      type="text"
                      id="subject"
                      name="subject"
                      placeholder={t("subject-placeholder")}
                      errorId="subject-error"
                      aria-required="true"
                      describedByIds={["subject-help"]}
                      className="w-full border border-primary-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="text" className="block font-semibold text-primary-700 mb-1 text-left">
                      <Translate text={"grievance-text"} /> <span className="text-red-600" aria-hidden="true">*</span>
                    </label>
                    <AccessibleField
                      as="textarea"
                      id="text"
                      name="text"
                      placeholder={t("text-placeholder")}
                      rows={4}
                      errorId="text-error"
                      aria-required="true"
                      describedByIds={["text-help"]}
                      className="w-full border border-primary-200 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-200"
                    />
                  </div>
                  <div>
                    <label htmlFor="file" className="block font-semibold text-primary-700 mb-1 text-left">
                      <Translate text={"upload-attachment"} /> <span className="text-gray-600 font-normal">{t('optional')}</span>
                    </label>
                    <input
                      type="file"
                      id="file"
                      name="file"
                      onChange={e => setFieldValue("file", e.target.files[0])}
                      className="w-full block"
                      aria-describedby="file-help"
                    />
                  </div>
                </fieldset>
              </div>
              <fieldset className="mt-8 border-0 p-0 m-0 min-w-0 md:col-span-2">
                <legend className="sr-only">{t('verification-submission')}</legend>
                <div className="flex flex-col md:flex-row md:items-end gap-4 w-full">
                  <div className="flex-1">
                    <label htmlFor="captcha" className="block font-semibold text-primary-700 text-left mb-1">
                      <Translate text={"enter-captcha"} /> <span className="text-red-600" aria-hidden="true">*</span>
                    </label>
                    <div className="flex flex-col md:flex-row md:items-center gap-3">
                      <canvas
                        ref={canvasRef}
                        width={130}
                        height={40}
                        className="border rounded bg-gray-50 flex-shrink-0"
                        role="img"
                        aria-label={t("captcha-aria-label")}
                      />
                      <div className="flex items-center gap-2">
                        <AccessibleField
                          type="text"
                          id="captcha"
                          name="captcha"
                          placeholder={t("captcha-placeholder")}
                          errorId="captcha-error"
                          aria-required="true"
                          describedByIds={["captcha-help"]}
                          autoComplete="off"
                          className="border border-primary-200 rounded px-3 py-2 w-32 focus:outline-none focus:ring-2 focus:ring-primary-200"
                        />
                        <button
                          type="button"
                          onClick={handleRefreshCaptcha}
                          className="text-primary-700 hover:text-primary-800 text-2xl focus:ring-2 focus:ring-primary-500 rounded px-2"
                          aria-label={t("refresh-captcha-title")}
                          title={t("refresh-captcha-title")}
                        >⟳</button>
                      </div>
                    </div>
                  </div>
                  <div className="flex-shrink-0 mt-4 md:mt-0">
                    <button
                      type="submit"
                      className="w-full md:w-auto bg-primary-700 hover:bg-primary-800 text-white font-semibold px-8 py-2.5 rounded shadow transition focus:ring-2 focus:ring-offset-2 focus:ring-primary-700"
                    >
                      <Translate text={"submit-form"} />
                    </button>
                  </div>
                </div>
              </fieldset>
            </Form>
          )}
        </Formik>
      </section>
    </div>
  );
}
